/**
 * JWT Token Management Utilities (V107: Username-based)
 *
 * LocalStorage keys (admin_* prefix for aicc-admin):
 * - admin_access_token: JWT access token
 * - admin_refresh_token: JWT refresh token
 * - admin_username: User username (로그인 ID - Primary)
 * - admin_userName: User display name (실명)
 * - admin_userEmail: User email (변경 가능)
 * - admin_userRoles: User roles (JSON string array)
 * - admin_userOrganization: User organization (JSON string)
 */

const TOKEN_KEYS = {
  ACCESS_TOKEN: 'admin_access_token',
  REFRESH_TOKEN: 'admin_refresh_token',
  USERNAME: 'admin_username',
  USER_NAME: 'admin_userName',
  USER_EMAIL: 'admin_userEmail',
  USER_ROLES: 'admin_userRoles',
  USER_ORGANIZATION: 'admin_userOrganization',
} as const;

/**
 * Get access token from localStorage
 */
export const getAccessToken = (): string | null => {
  return localStorage.getItem(TOKEN_KEYS.ACCESS_TOKEN);
};

/**
 * Get refresh token from localStorage
 */
export const getRefreshToken = (): string | null => {
  return localStorage.getItem(TOKEN_KEYS.REFRESH_TOKEN);
};

/**
 * Set access token to localStorage
 */
export const setAccessToken = (token: string): void => {
  localStorage.setItem(TOKEN_KEYS.ACCESS_TOKEN, token);
};

/**
 * Set refresh token to localStorage
 */
export const setRefreshToken = (token: string): void => {
  localStorage.setItem(TOKEN_KEYS.REFRESH_TOKEN, token);
};

/**
 * Set all authentication tokens and user info (V107: Username-based)
 */
export const setTokens = (
  accessToken: string,
  refreshToken: string,
  username: string,
  name: string,
  email: string,
  roles: string[],
  organization?: any
): void => {
  localStorage.setItem(TOKEN_KEYS.ACCESS_TOKEN, accessToken);
  localStorage.setItem(TOKEN_KEYS.REFRESH_TOKEN, refreshToken);
  localStorage.setItem(TOKEN_KEYS.USERNAME, username);  // V107: Primary identifier
  localStorage.setItem(TOKEN_KEYS.USER_NAME, name);
  localStorage.setItem(TOKEN_KEYS.USER_EMAIL, email);
  localStorage.setItem(TOKEN_KEYS.USER_ROLES, JSON.stringify(roles));
  if (organization) {
    localStorage.setItem(TOKEN_KEYS.USER_ORGANIZATION, JSON.stringify(organization));
  }
};

/**
 * Remove all authentication tokens and user info (V107: Username-based)
 */
export const removeTokens = (): void => {
  localStorage.removeItem(TOKEN_KEYS.ACCESS_TOKEN);
  localStorage.removeItem(TOKEN_KEYS.REFRESH_TOKEN);
  localStorage.removeItem(TOKEN_KEYS.USERNAME);
  localStorage.removeItem(TOKEN_KEYS.USER_NAME);
  localStorage.removeItem(TOKEN_KEYS.USER_EMAIL);
  localStorage.removeItem(TOKEN_KEYS.USER_ROLES);
  localStorage.removeItem(TOKEN_KEYS.USER_ORGANIZATION);
};

/**
 * Get user info from localStorage (V107: Username-based)
 */
export const getUserInfo = () => {
  const username = localStorage.getItem(TOKEN_KEYS.USERNAME);
  const name = localStorage.getItem(TOKEN_KEYS.USER_NAME);
  const email = localStorage.getItem(TOKEN_KEYS.USER_EMAIL);
  const rolesStr = localStorage.getItem(TOKEN_KEYS.USER_ROLES);
  const organizationStr = localStorage.getItem(TOKEN_KEYS.USER_ORGANIZATION);

  let roles: string[] = [];
  if (rolesStr) {
    try {
      roles = JSON.parse(rolesStr);
    } catch (error) {
      console.error('Failed to parse user roles:', error);
    }
  }

  let organization = null;
  if (organizationStr) {
    try {
      organization = JSON.parse(organizationStr);
    } catch (error) {
      console.error('Failed to parse organization:', error);
    }
  }

  return { username, name, email, roles, organization };
};

/**
 * Check if user is authenticated (has access token)
 */
export const isAuthenticated = (): boolean => {
  return !!getAccessToken();
};

/**
 * Parse JWT token and extract payload
 */
export const parseJwt = (token: string): any => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Failed to parse JWT token:', error);
    return null;
  }
};

/**
 * Check if token is expired
 */
export const isTokenExpired = (token: string): boolean => {
  const payload = parseJwt(token);
  if (!payload || !payload.exp) {
    return true;
  }

  // exp is in seconds, Date.now() is in milliseconds
  const expirationTime = payload.exp * 1000;
  const currentTime = Date.now();

  // Consider token expired if it expires within 60 seconds
  return currentTime >= expirationTime - 60000;
};

/**
 * Check if access token needs refresh
 */
export const needsTokenRefresh = (): boolean => {
  const accessToken = getAccessToken();
  if (!accessToken) {
    return false;
  }
  return isTokenExpired(accessToken);
};
